import React, { useCallback, useMemo, useState } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import _, { isArray } from 'lodash';
import { useSelector } from 'react-redux';
import { FemaleIcon, MaleIcon } from '../../../components/Icon';
import { ReactComponent as Xmale } from '../../../static/images/Xmale.svg';
import { BED_STATUS_CUBICLE_COLOR, BED_STATUS_REMARK_COLOR, BS_TOOLBAR_STATUS } from '../../../comm/constant';
import { bedStatusAPI } from '../../../apis';
import { alertMsg, ResponseCommCodeEnum } from '../../../apis/commlogic';
import { i18n } from '../../../language';
import Dialog from '../../../components/Dialog';
import { handleBedsFilter } from '../../../utils/newBedStatusUtil';
import { classNames, onKeyUpEnterOrSpace } from '../../../utils';
import useCheckLogin from '../../../utils/useCheckLogin';
import { BedStatusIcon } from '../components';
import EmptyBed from './Icons/EmptyBed';
import EditCubicle from './Popups/EditCubicle';
import NurseStation from './NurseStation';
import UpdateCubicle from './Popups/UpdateCubicle';
import LinkedBed from './Popups/LinkedBed';
import LandingBed from './Icons/LandingBed';
import Memo from './Memo';

const useStyles = makeStyles(() => ({
  root: ({ color, borderLine, isCubicleV2 }) => ({
    padding: isCubicleV2 ? '0.285rem 0.428rem' : '0.571rem 0.813rem 0.571rem 0.688rem',
    borderRadius: '8px',
    backgroundColor: BED_STATUS_CUBICLE_COLOR[color] ?? '#fff',
    display: 'inline-flex',
    flexDirection: 'column',
    // minWidth: '11rem',
    minHeight: isCubicleV2 ? '6.888rem' : '9.3rem',
    border: borderLine === 'show' ? 'solid 1px #AAAAAA' : 'unset',
  }),
  maleIcon: {
    fontSize: '1rem',
    color: '#538fff',
    marginRight: '0.25rem',
  },
  femaleIcon: {
    fontSize: '1rem',
    color: '#ff8181',
    marginRight: '0.25rem',
  },
  xmaleIcon: {
    fontSize: '1rem',
    color: '#a375ff',
  },
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.875rem',
    color: '#2e4469',
    lineHeight: '1.05rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  subTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.75rem',
    color: '#2e4469',
  },
  status: {
    backgroundColor: '#979bff',
    padding: '0.125rem 0.25rem',
    borderRadius: '2px',
  },
  statusContent: {
    color: '#fff',
    fontSize: '0.75rem',
    fontFamily: 'NotoSans-Medium',
  },
  tipBarBox: ({ isCubicleV2 }) => ({
    margin: isCubicleV2 ? '0.142rem 0' : '0.5rem 0',
    height: isCubicleV2 ? '1.321rem' : '2rem',
  }),
  tipBar: ({ remarkColor, isCubicleV2 }) => ({
    backgroundColor: BED_STATUS_REMARK_COLOR[remarkColor]?.bgColor ?? '#fff',
    borderRadius: '4px',
    border: 'solid 1px #e4e7eb',
    padding: isCubicleV2 ? '0.1rem 0.5rem' : '0.25rem 0.5rem',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    overflow: 'hidden',
    '&::before': {
      position: 'absolute',
      top: '0',
      left: '0',
      content: '""',
      display: 'block',
      width: '0.25rem',
      height: '100%',
      backgroundColor: BED_STATUS_REMARK_COLOR[remarkColor]?.leftColor ?? '#fff',
      zIndex: '9',
    },
  }),
  tips: ({ remarkColor, isCubicleV2 }) => ({
    fontFamily: 'NotoSans-Medium',
    color: BED_STATUS_REMARK_COLOR[remarkColor]?.color ?? '#2e4469',
    fontSize: '0.75rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: isCubicleV2 ? 1.2 : 'normal',
  }),
  border: ({ isCubicleV2 }) => ({
    width: '100%',
    height: isCubicleV2 ? '0.063rem' : '2rem',
    backgroundColor: isCubicleV2 ? '#e4e7eb' : 'unset',
    margin: isCubicleV2 ? '0.285rem 0' : '0',
  }),
  popupContent: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.25rem',
    color: '#2e4469',
  },
  clickStyle: {
    '&:hover': {
      cursor: 'default',
    },
  },
}));

const Cubicle = (props) => {
  const {
    name,
    gender,
    type,
    status,
    remarks,
    color,
    remarkColor,
    style = {},
    bedArrangement,
    id,
    beds = [],
    edit = true,
    getFloorPlanListAPI,
    handleUpdateCubicle,
    handleDeleteCubicle,
    handleEditCubicle,
    tool,
    handleDeleteBed,
    handleUpdateLink,
    handleRemoveLinkedBed,
    setPopup,
    borderLine,
    filterData,
    handleCopyCubicle,
    header,
    readOnly = false,
    hospCode,
    wardLocation,
    showPatientName,
    isMutipleWardCode,
  } = props;
  const { isCubicleV2 } = useSelector((state) => state.bedStatusView);
  const classes = useStyles({ color, remarkColor, borderLine, isCubicleV2 });
  const [editOpen, setEditOpen] = useState(false);
  const [deleteSinglePopup, setDeleteSinglePopup] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState({
    cubicleId: id,
    index: null,
  });
  const [linkBedPopup, setLinkBedPopup] = useState(false);
  const [bedData, setBedData] = useState(null);
  const checkLogin = useCheckLogin();

  const handleClose = useCallback(() => {
    setEditOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    if (checkLogin() && !readOnly) {
      setEditOpen(true);
    }
  }, [checkLogin, readOnly]);

  const updateBedStatus = async (data, closePop) => {
    try {
      await bedStatusAPI.postUpdateStatus(data);
      try {
        await getFloorPlanListAPI(closePop);
      } catch {
        closePop();
      }
    } catch (e) {
      if (e.statusCode === ResponseCommCodeEnum.BED_VERSION) {
        await getFloorPlanListAPI();
        alertMsg(
          i18n.t(
            'BEDSTATUS_BED_STATUS_OF_THIS_BED_WAS_CHANGED_BY_OTHER_WARDPLACE_USER_OR_IPAS_PLEASE_REFRESH_WEB_PAGE_TO_GET_THE_LATEST_BED_STATUS_INFO_AND_CHECK_IF_FURTHER_ACTION_IS_REQUIRED'
          ),
          _.uniqueId('warning_'),
          'warning'
        );
      }
    }
  };

  const saveBedStatus = async (data, closePop) => {
    try {
      await bedStatusAPI.postSaveRemark({ ...data, ccWardCd: data.wardCd });
      try {
        await getFloorPlanListAPI(closePop);
      } catch {
        closePop();
      }
    } catch (e) {
      if (e.statusCode === ResponseCommCodeEnum.BED_VERSION) {
        await getFloorPlanListAPI();
        alertMsg(
          i18n.t(
            'BEDSTATUS_BED_STATUS_OF_THIS_BED_WAS_CHANGED_BY_OTHER_WARDPLACE_USER_OR_IPAS_PLEASE_REFRESH_WEB_PAGE_TO_GET_THE_LATEST_BED_STATUS_INFO_AND_CHECK_IF_FURTHER_ACTION_IS_REQUIRED'
          ),
          _.uniqueId('warning_'),
          'warning'
        );
      }
    }
  };
  const saveReturnDayCase = async (data, closePop) => {
    try {
      await bedStatusAPI.postSaveReturnDayCase(data);
      try {
        await getFloorPlanListAPI();
      } catch (error) {}
    } catch (error) {}
  };

  const EditPopup = useMemo(() => {
    if (edit) {
      return EditCubicle;
    } else {
      return UpdateCubicle;
    }
  }, [edit]);

  const handleClickBed = useCallback(
    (bedData, index, row, column) => () => {
      if (!edit) {
        return;
      }
      if (bedData.linkeds.length > 1) {
        setLinkBedPopup(true);
        setBedData({ ...bedData, bedRow: row, bedColumn: column });
      } else {
        setDeleteSinglePopup(true);
        setDeleteInfo((o) => ({
          ...o,
          index,
        }));
      }
    },
    [edit]
  );

  const handleCloseDeleteBedPopup = useCallback(() => {
    setDeleteSinglePopup(false);
    setDeleteInfo((o) => ({
      ...o,
      index: null,
    }));
  }, []);

  const deleteBed = useCallback(() => {
    handleDeleteBed(deleteInfo);
    handleCloseDeleteBedPopup();
  }, [handleDeleteBed, deleteInfo, handleCloseDeleteBedPopup]);

  const handleCloseLinkBedPopup = useCallback(() => {
    setLinkBedPopup(false);
    setBedData(null);
  }, []);

  const bedsFilter = useMemo(() => {
    if (edit) {
      return beds;
    } else {
      return _.map(beds, (item) => ({
        ...item,
        data: item.data && handleBedsFilter(filterData, item.data) ? item.data : null,
      }));
    }
  }, [beds, filterData, edit]);

  const showHeader = useMemo(() => header === 'show', [header]);

  return (
    <>
      {type === 'Nurse Station/Misc.' && (
        <NurseStation
          {...props}
          edit={edit}
          handleDeleteCubicle={handleDeleteCubicle}
          setPopup={setPopup}
          handleCopyCubicle={handleCopyCubicle}
          readOnly={readOnly}
          checkLogin={checkLogin}
        />
      )}
      {type !== 'Nurse Station/Misc.' && type !== 'Memo' && (
        <div className={classes.root} style={style}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box display="flex" flexDirection="column">
              <Box display="flex" alignItems="center">
                <ButtonBase onClick={handleOpen}>
                  {gender === 'M' && <MaleIcon className={classes.maleIcon} />}
                  {gender === 'F' && <FemaleIcon className={classes.femaleIcon} />}
                  {gender === 'X' && <Xmale className={classes.xmaleIcon} />}
                </ButtonBase>
                {showHeader && (
                  <div
                    className={classNames(classes.title, classes.clickStyle)}
                    onClick={handleOpen}
                    tabIndex={0}
                    onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpen)}
                  >
                    {name}
                  </div>
                )}
              </Box>
              {showHeader && type && (
                <Box display="flex" alignItems="center" mt="0.142rem" height="0.9rem">
                  <div
                    className={classNames(classes.subTitle, classes.clickStyle)}
                    onClick={handleOpen}
                    tabIndex={0}
                    onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpen)}
                  >
                    {type}
                  </div>
                </Box>
              )}
            </Box>
            {status && showHeader && (
              <Box display="flex" alignItems="center">
                <div
                  className={classNames(classes.status, classes.clickStyle)}
                  onClick={handleOpen}
                  tabIndex={0}
                  onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpen)}
                >
                  <div className={classes.statusContent}>{status}</div>
                </div>
              </Box>
            )}
          </Box>
          {showHeader && remarks ? (
            <Box className={classes.tipBarBox}>
              <Box position="relative">
                <div
                  className={classNames(classes.tipBar, classes.clickStyle)}
                  onClick={handleOpen}
                  tabIndex={0}
                  onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpen)}
                >
                  <div className={classes.tips} title={remarks}>
                    {remarks}
                  </div>
                </div>
              </Box>
            </Box>
          ) : (
            <div className={classes.border} />
          )}
          {isArray(bedsFilter) && bedsFilter.length > 0 && (
            <Box
              display="grid"
              gridTemplateRows={`repeat(${bedArrangement.row}, 1fr)`}
              gridTemplateColumns={`repeat(${bedArrangement.column}, 1fr)`}
              gridGap="0.2rem"
            >
              {bedsFilter.map((item, index) =>
                edit ? (
                  <Droppable
                    key={item.key}
                    droppableId={JSON.stringify({
                      cubicleId: id,
                      row: item.row,
                      column: item.column,
                      type: 'Cubicle',
                    })}
                    renderClone={(provided) => (
                      <div {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
                        <BedStatusIcon
                          isAbsolute={true}
                          allData={item.data}
                          status={item.data.status}
                          bedNo={item.data.bedNo}
                          gender={item.data.patGender}
                          ward={item.data.ccWardCd}
                          disabled
                          editFloorPlan
                        />
                      </div>
                    )}
                  >
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {item.data ? (
                          <Box
                            width="5rem"
                            height="4.688rem"
                            pt="1.563rem"
                            onClick={handleClickBed(item.data, index, item.row, item.column)}
                          >
                            <Draggable
                              draggableId={item.data.bedStatusId.toString()}
                              index={index}
                              isDragDisabled={tool.type === BS_TOOLBAR_STATUS.MOVE}
                            >
                              {(dragProvided) => {
                                const style = {
                                  ...dragProvided.draggableProps.style,
                                  transform: snapshot.isDraggingOver
                                    ? 'unset'
                                    : dragProvided.draggableProps.style.transform,
                                };
                                return (
                                  <div
                                    {...dragProvided.draggableProps}
                                    {...dragProvided.dragHandleProps}
                                    ref={dragProvided.innerRef}
                                    style={style}
                                  >
                                    <BedStatusIcon
                                      isAbsolute={true}
                                      allData={item.data}
                                      status={item.data.status}
                                      bedNo={item.data.bedNo}
                                      gender={item.data.patGender}
                                      ward={item.data.ccWardCd}
                                      reload={getFloorPlanListAPI}
                                      disabled
                                      editFloorPlan
                                    />
                                  </div>
                                );
                              }}
                            </Draggable>
                          </Box>
                        ) : (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            width="5rem"
                            height="4.688rem"
                          >
                            <EmptyBed />
                          </Box>
                        )}
                        {provided.placeholder && <div style={{ display: 'none' }}>{provided.placeholder}</div>}
                      </div>
                    )}
                  </Droppable>
                ) : (
                  <LandingBed
                    data={item.data}
                    updateBedStatus={updateBedStatus}
                    saveBedStatus={saveBedStatus}
                    saveReturnDayCase={saveReturnDayCase}
                    getFloorPlanListAPI={getFloorPlanListAPI}
                    key={item.key}
                    readOnly={readOnly}
                    showPatientName={showPatientName}
                    isMutipleWardCode={isMutipleWardCode}
                  />
                )
              )}
            </Box>
          )}
        </div>
      )}
      {type === 'Memo' && <Memo edit={edit} readOnly={readOnly} {...props} />}

      {editOpen && (
        <EditPopup
          edit={edit}
          handleClose={handleClose}
          data={props}
          handleUpdateCubicle={handleUpdateCubicle}
          handleDeleteCubicle={handleDeleteCubicle}
          handleEditCubicle={handleEditCubicle}
          setPopup={setPopup}
          handleCopyCubicle={handleCopyCubicle}
          hospCode={hospCode}
          wardLocation={wardLocation}
        />
      )}
      <Dialog
        open={deleteSinglePopup}
        title="Remove Bed"
        cancel="Cancel"
        confirm="Remove"
        cancelStyle={{ border: 'unset' }}
        confirmStyle={{ backgroundColor: '#ff655c' }}
        handleCancel={handleCloseDeleteBedPopup}
        handleClose={handleCloseDeleteBedPopup}
        handleConfirm={deleteBed}
      >
        <div className={classes.popupContent}>Are you sure to remove the bed from the cubicle?</div>
        <div className={classes.popupContent}>Bed will be placed at waiting list if removed.</div>
      </Dialog>
      {linkBedPopup && (
        <LinkedBed
          bedData={bedData}
          handleClose={handleCloseLinkBedPopup}
          handleUpdateLink={handleUpdateLink}
          handleRemoveLinkedBed={handleRemoveLinkedBed}
          id={id}
        />
      )}
    </>
  );
};

export default Cubicle;
